import { CommonModule } from '@angular/common';
import {
    Component,
    Input,
    InputSignal,
    OnDestroy,
    Signal,
    WritableSignal,
    computed,
    inject,
    input,
    signal,
    CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import { IqControlsDashboardComponent } from '@dat/iq-controls';
import {
    EventInfo,
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
    IqEventBusNode,
    IqEventBusTransformationService,
    matchQuery,
} from '@dat/iq-event-bus';
import {
    IqGraphLtLoadsComponent,
    IqGraphLtTrucksComponent,
} from '@dat/iq-graph-lt';
import {
    IqGraphRatesAllComponent,
    IqGraphRatesCurrentComponent,
    IqGraphRatesHybridComponent,
    IqGraphRatesThirteenMonthForecastComponent,
    IqGraphRatesThirteenMonthHistoricalComponent,
    IqGraphRatesThirtyFiveDayForecastComponent,
    IqGraphRatesTimeComponent,
} from '@dat/iq-graph-rates';
import { IqLinksBlogLinksComponent } from '@dat/iq-links-blog';
import {
    IqMcGaugeQueryLtComponent,
    IqMcGaugeQueryMciComponent,
} from '@dat/iq-mc-gauge-query';
import {
    IqMcMapLtComponent,
    IqMcMapMciComponent,
    IqMcMapMciDashboardComponent,
} from '@dat/iq-mc-map';
import { IqMcMapLaneMapComponent } from '@dat/iq-mc-map-lane';
import { IqMediaVideoComponent } from '@dat/iq-media';
import { IqNtlGraphRatesMultiComponent } from '@dat/iq-ntl-graph-rates';
import {
    IqRateLaneUtilsFuelLoaderComponent,
    IqRateLaneUtilsMileageLoaderComponent,
    IqRateLaneUtilsODSearchFormatterComponent,
    IqRateLaneUtilsRateLoaderComponent,
} from '@dat/iq-rate-lane-utils';
import { IqRateNtlCardComponent } from '@dat/iq-rate-ntl';
import {
    IqSocialLinksHelpCenterComponent,
    IqSocialLinksPodcastComponent,
} from '@dat/iq-social-links';
import {
    IqStatusLibraryCardComponent,
    IqStatusLibraryCardGridComponent,
    IqStatusLibraryTableComponent,
    IqStatusLibraryVisualizationComponent,
} from '@dat/iq-status-library';
import {
    IqBuilderLabelComponent,
    IqCssClassProcessorDirective,
    IqStyleProcessorShowElementComponent,
} from '@dat/iq-style-processor';
import { IqTableContributionsTableComponent } from '@dat/iq-table-contributions';
import {
    IqTableRateForecast13mComponent,
    IqTableRateForecast35dComponent,
    IqTableRateHistorical13mComponent,
    IqTableRateSharedComponent,
} from '@dat/iq-table-rate';
import { IqTableRateRecentSearchComponent } from '@dat/iq-table-rate-recent-search';
import { IqTrendlinesContentViewTrendsComponent } from '@dat/iq-trendlines-content';
import { IqTrendlinesFuelCardComponent } from '@dat/iq-trendlines-fuel';
import {
    IqTrendlinesGraphLtFlatbedComponent,
    IqTrendlinesGraphLtReeferComponent,
    IqTrendlinesGraphLtVanComponent,
} from '@dat/iq-trendlines-graph-lt';
import {
    IqTrendlinesGraphLtNtlFlatbedComponent,
    IqTrendlinesGraphLtNtlReeferComponent,
    IqTrendlinesGraphLtNtlVanComponent,
} from '@dat/iq-trendlines-graph-lt-ntl';
import {
    IqTrendlinesGraphRatesFlatbedComponent,
    IqTrendlinesGraphRatesReeferComponent,
    IqTrendlinesGraphRatesVanComponent,
} from '@dat/iq-trendlines-graph-rates';
import {
    IqTrendlinesGraphRatesNtlFlatbedComponent,
    IqTrendlinesGraphRatesNtlReeferComponent,
    IqTrendlinesGraphRatesNtlVanComponent,
} from '@dat/iq-trendlines-graph-rates-ntl';
import {
    IqTrendlinesMapLtFlatbedComponent,
    IqTrendlinesMapLtReeferComponent,
    IqTrendlinesMapLtVanComponent,
    IqTrendlinesMapRateFlatbedComponent,
    IqTrendlinesMapRateReeferComponent,
    IqTrendlinesMapRateVanComponent,
} from '@dat/iq-trendlines-map';
import {
    IqTrendlinesNtlSpotFlatbedComponent,
    IqTrendlinesNtlSpotReeferComponent,
    IqTrendlinesNtlSpotVanComponent,
} from '@dat/iq-trendlines-ntl-spot';
import { IqTrendlinesSnapshotTableComponent } from '@dat/iq-trendlines-snapshot';
import { IqTrendlinesSocialWeeklyBriefComponent } from '@dat/iq-trendlines-social';
import { camelCase, get, omit, set } from 'lodash';
import { isEmpty } from 'ramda';
import { Component as ComponentType, RouteInfo, TriggerEvent } from '../models';
import { IqBuilderStateRulesService, IqBuilderStateService } from '../state';
import { IqMcEventBusRegisterComponent } from '@dat/iq-mc-event-bus';
import { IqBuilderAuditToolsService } from '../iq-builder-audit-tools/iq-builder-audit-tools.service';
import {
    IqMcGaugeMciComponent,
    IqMcGaugeSectionComponent,
} from '@dat/iq-mc-gauge';
import {
    IqMcChartsLtComponent,
    IqMcChartsMciComponent,
} from '@dat/iq-mc-charts';
import { IqMcModalComponent } from '@dat/iq-mc-modal';
import { IqMcLabelCapacityComponent } from '@dat/iq-mc-label';
import { IqStateRegionMatrixCardComponent } from '@dat/iq-state-region-matrix';
import { IqTrendlinesUpsellBannerComponent } from '@dat/iq-trendlines-upsell';
import { IqTrendlinesFfRegisterComponent } from '@dat/iq-trendlines-ff';

type RuleResponseTypes = 'ACTION';
type BuilderComponentEvaluatorSuccessPayload = EventInfo & {
    data: {
        [key: string]: any;
        dashboardId: string;
        sectionId: string;
        type: RuleResponseTypes;
    };
};

/**
 * TemplateData is a type that represents the data that is used to render
 * the events
 */
type TemplateData = {
    /**
     * The current information that is stored at the dashboard
     * level
     */
    dashboardCache: any;
    /**
     * The current information that is stored at the section level
     */
    sectionCache: any;
    /**
     * The current information that is stored at the component level
     */
    cache: any;
    /**
     * If this component is leveraging an Angular Routing mechanism, this is the
     * information that is used to render the route
     */
    route: RouteInfo;
    /**
     * The dashboard id that this component is associated with
     */
    dashboardId: string;
    /**
     * The section id that this component is associated with
     */
    sectionId: string;
};

@Component({
    selector: 'iq-builder-component',
    standalone: true,
    imports: [
        CommonModule,
        IqMcGaugeQueryLtComponent,
        IqBuilderLabelComponent,
        IqMcGaugeQueryMciComponent,
        IqCssClassProcessorDirective,
        IqMcMapLaneMapComponent,
        IqMcMapMciComponent,
        IqTableRateSharedComponent,
        IqStyleProcessorShowElementComponent,
        IqTableRateForecast13mComponent,
        IqTableRateForecast35dComponent,
        IqTrendlinesSocialWeeklyBriefComponent,
        IqTableRateHistorical13mComponent,
        IqTrendlinesNtlSpotFlatbedComponent,
        IqTrendlinesSnapshotTableComponent,
        IqTrendlinesNtlSpotReeferComponent,
        IqTrendlinesNtlSpotVanComponent,
        IqTrendlinesFuelCardComponent,
        IqControlsDashboardComponent,
        IqRateNtlCardComponent,
        IqGraphRatesThirteenMonthForecastComponent,
        IqGraphRatesThirteenMonthHistoricalComponent,
        IqGraphRatesThirtyFiveDayForecastComponent,
        IqGraphRatesHybridComponent,
        IqTrendlinesGraphRatesFlatbedComponent,
        IqTrendlinesGraphRatesVanComponent,
        IqTrendlinesGraphRatesReeferComponent,
        IqTrendlinesGraphRatesNtlReeferComponent,
        IqTrendlinesGraphRatesNtlFlatbedComponent,
        IqTrendlinesGraphRatesNtlVanComponent,
        IqTrendlinesGraphLtNtlFlatbedComponent,
        IqTrendlinesGraphLtNtlVanComponent,
        IqTrendlinesGraphLtNtlReeferComponent,
        IqTrendlinesGraphLtReeferComponent,
        IqTrendlinesGraphLtFlatbedComponent,
        IqTrendlinesGraphLtVanComponent,
        IqTrendlinesMapLtFlatbedComponent,
        IqTrendlinesMapLtReeferComponent,
        IqTrendlinesMapLtVanComponent,
        IqTrendlinesMapRateFlatbedComponent,
        IqTrendlinesMapRateReeferComponent,
        IqTrendlinesContentViewTrendsComponent,
        IqTrendlinesMapRateVanComponent,
        IqLinksBlogLinksComponent,
        IqSocialLinksPodcastComponent,
        IqSocialLinksHelpCenterComponent,
        IqMediaVideoComponent,
        IqGraphLtLoadsComponent,
        IqGraphLtTrucksComponent,
        IqNtlGraphRatesMultiComponent,
        IqMcMapMciDashboardComponent,
        IqGraphRatesHybridComponent,
        IqTableContributionsTableComponent,
        IqRateLaneUtilsODSearchFormatterComponent,
        IqRateLaneUtilsFuelLoaderComponent,
        IqTableRateRecentSearchComponent,
        MatIconModule,
        IqRateLaneUtilsMileageLoaderComponent,
        IqRateLaneUtilsRateLoaderComponent,
        IqStatusLibraryCardComponent,
        IqStatusLibraryVisualizationComponent,
        IqStatusLibraryCardGridComponent,
        IqStatusLibraryTableComponent,
        IqMcEventBusRegisterComponent,
        IqGraphRatesCurrentComponent,
        IqGraphRatesAllComponent,
        IqGraphRatesTimeComponent,
        IqMcGaugeMciComponent,
        IqMcGaugeSectionComponent,
        IqMcChartsLtComponent,
        IqMcChartsMciComponent,
        IqMcModalComponent,
        IqMcMapLtComponent,
        IqMcLabelCapacityComponent,
        IqStateRegionMatrixCardComponent,
        IqTrendlinesUpsellBannerComponent,
        IqTrendlinesFfRegisterComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './iq-builder-component.component.html',
    styleUrl: './iq-builder-component.component.scss',
})
export class IqBuilderComponentComponent implements OnDestroy {
    @Input({ required: true }) id: string;
    @Input({ required: true }) dashboardId: string;
    @Input({ required: true }) sectionId: string;
    private readonly _eventBroker: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    private readonly _transform: IqEventBusTransformationService = inject(
        IqEventBusTransformationService,
    );

    component: InputSignal<ComponentType> = input(null);

    dashboardCache: Signal<any> = computed(() => {
        return this._state.dashboardCache(this.dashboardId)();
    });
    sectionCache: Signal<any> = computed(() => {
        return this._state.sectionCache(this.dashboardId, this.sectionId)();
    });
    componentCache: Signal<any> = computed(() => {
        return this.component().cache;
    });

    props: Signal<Record<string, any>> = computed(() => {
        const templateData = this.templateData();
        const mappedKeys = Object.keys(this.component().propMap ?? {});
        const updatedProps = mappedKeys.reduce(
            (acc, key) => {
                return set(
                    { ...acc },
                    key,
                    get(templateData, this.component().propMap[key], null),
                );
            },
            {
                ...this.component().props,
            },
        );
        return updatedProps;
    });

    /**
     * A signal that has the current status of the template data used
     * for rendering components.
     *
     *
     */
    templateData: Signal<TemplateData> = computed((): TemplateData => {
        const dashboardCache = this.dashboardCache();
        const sectionCache = this.sectionCache();

        return {
            dashboardCache,
            sectionCache,
            cache: this.component().cache,
            route: this._routeInfo(),
            dashboardId: this.dashboardId,
            sectionId: this.sectionId,
        };
    });

    private _deregister: () => void;
    private _registeredAction: Record<string, IqEventBusBrokerHandler>;

    private readonly _routeInfo: WritableSignal<RouteInfo> = signal<RouteInfo>({
        analyticsPageName: '',
        data: {},
        locked: false,
        pageTitle: '',
        params: {},
        path: '',
        loaded: false,
    });

    private _auditToolService = inject(IqBuilderAuditToolsService);

    // Type State
    // Prop State
    // id State

    constructor(
        private readonly _state: IqBuilderStateService,
        private readonly _builderRules: IqBuilderStateRulesService,
    ) {
        this._deregister = this._eventBroker.watchHandledEvents(
            (node: IqEventBusNode) => {
                if (!node.evt?.name?.startsWith('type:')) return;
                // console.log(node.evt.name, node.evt)
                if (node.evt.name === 'type:graph-rates-time-view-update') {
                    // console.log(node.evt)
                }
                const eventName = camelCase(
                    node.evt.name.replace('type', 'on'),
                );
                const actionArray = this.component()?.[eventName] ?? [];

                if (!actionArray.length) return;

                const templateData = this.getTemplateData(node.evt.payload);

                this._auditToolService.addPrerenderedAction({
                    id: node.id,
                    template: JSON.stringify(actionArray, null, 4),
                    data: templateData,
                    eventName,
                });
                const filteredActions: TriggerEvent[] =
                    actionArray
                        ?.filter?.((action: TriggerEvent) =>
                            !isEmpty(action.sourceEventRule)
                                ? matchQuery(
                                      node.evt,
                                      this._transform.renderJson(
                                          JSON.stringify(
                                              action.sourceEventRule ?? {},
                                              null,
                                              4,
                                          ),
                                          {
                                              ...templateData,
                                              dashboardId: this.dashboardId,
                                              sectionId: this.sectionId,
                                              componentId: this.id,
                                          },
                                          {},
                                          (
                                              err: any,
                                              template: string,
                                              data: any,
                                          ) => {
                                              this._logFilterActionsErrors(
                                                  eventName,
                                                  node,
                                                  err,
                                                  template,
                                                  data,
                                              );
                                          },
                                      ) ?? {},
                                      action.sourceEventRuleOperator,
                                  )
                                : true,
                        )
                        ?.map((x) => omit(x, ['sourceEventRule'])) ?? [];

                if (isEmpty(filteredActions)) return;

                const renderedActions = this._transform
                    .renderJson(
                        JSON.stringify(filteredActions, null, 4),
                        {
                            ...templateData,
                            dashboardId: this.dashboardId,
                            sectionId: this.sectionId,
                            componentId: this.id,
                        },
                        {},
                        (err: any, template: string, data: any) => {
                            this._logRenderedErrors(
                                eventName,
                                node,
                                err,
                                template,
                                data,
                            );
                        },
                    )
                    .map(
                        (evt: EventInfo): EventInfo => ({
                            ...evt,
                            parentId: node.id,
                            meta: {
                                componentId: this.id,
                                sectionId: this.sectionId,
                                dashboardId: this.dashboardId,
                            },
                        }),
                    );

                this._state.processActions(renderedActions, {
                    ...templateData,
                    dashboardId: this.dashboardId,
                    sectionId: this.sectionId,
                    componentId: this.id,
                    type: 'ACTION' as RuleResponseTypes,
                });
            },
        );

        setTimeout(() => {
            this._registeredAction = this._eventBroker.registerMap({
                'type:rules:evaluator:run:success': {
                    callback: (
                        eventInfo: BuilderComponentEvaluatorSuccessPayload,
                    ) => {
                        this._runRules(eventInfo);
                    },
                },
                'type:route:changed': {
                    callback: (routeInfo: RouteInfo) => {
                        this._routeInfo.set({
                            ...routeInfo,
                            loaded: true,
                        });
                    },
                },
            });
        });
    }

    ngOnDestroy() {
        this._deregister?.();
        this._eventBroker.unregisterMap(this._registeredAction);
    }

    private _logFilterActionsErrors(
        eventName: string,
        node: IqEventBusNode,
        err: any,
        template: string,
        data: any,
    ) {
        this._auditToolService.addFilterActionsError({
            id: node.id,
            template,
            data,
            eventName,
            error: err,
        });
        throw err;
    }
    private _logRenderedErrors(
        eventName: string,
        node: IqEventBusNode,
        err: any,
        template: string,
        data: any,
    ) {
        this._auditToolService.addRenderedActionError({
            id: node.id,
            template,
            data,
            eventName,
            error: err,
        });
        throw err;
    }

    onReset($event: any) {}

    onChanged($event: any, onControlChange: any[] = []) {
        // const onChange = this.component().onChange ?? [];
        // if (!onControlChange?.length && !onChange.length) return;
        // const templateData = this.getTemplateData($event);
        // this._state.processActions(
        //     [
        //         ...onControlChange,
        //         ...onChange,
        //     ] asany[],
        //     templateData,
        // );
    }

    keyUp($event: any, onKeyUp: any[] = []): void {
        // if (!onKeyUp?.length) return;
        // const templateData = this.getTemplateData($event);
        // this._state.processActions(
        //     [...onKeyUp] asany[],
        //     templateData,
        // );
    }

    onLoaded($event: any, onLoad: any[] = []) {
        // if (!onLoad?.length && !this.component()?.onLoad?.length) return;
        // const loadActions = !!onLoad?.length ? onLoad : this.component().onLoad;
        // const templateData = this.getTemplateData($event);
        // this._state.processActions(loadActions, templateData);
    }

    onClick($event: any, onControlClick: any[] = []) {
        // const onClick = this.component().onClick ?? [];
        // if (!onControlClick?.length && !onClick.length) return;
        // const templateData = this.getTemplateData($event);
        // this._state.processActions(
        //     [...onClick, ...onControlClick] asany[],
        //     templateData,
        // );
    }

    onErrored($event: any, onError: any[] = []) {
        // if (!onError?.length && !this.component()?.onError?.length) return;
        // const errorActions = !!onError?.length
        //     ? onError
        //     : this.component().onError;
        // const templateData = this.getTemplateData($event);
        // this._state.processActions(errorActions, templateData);
    }

    onOpen($event: any = {}, actions: any[] = []): void {
        // if (
        //     !isEmpty(actions) ||
        //     (!isNil(this.component().onOpen) &&
        //         !isEmpty(this.component().onOpen))
        // ) {
        //     this._state.processActions(
        //         isEmpty(actions) ? this.component().onOpen : actions,
        //         this.getTemplateData($event),
        //     );
        // }
    }

    onClose($event: any = {}, actions: any[] = []): void {
        // if (
        //     !isEmpty(actions) ||
        //     (!isNil(this.component().onClose) &&
        //         !isEmpty(this.component().onClose))
        // ) {
        //     this._state.processActions(
        //         isEmpty(actions) ? this.component().onClose : actions,
        //         this.getTemplateData($event),
        //     );
        // }
    }

    private getTemplateData($event: any) {
        return {
            ...$event,
            ...this.templateData(),
        };
    }

    private _runRules(eventInfo: BuilderComponentEvaluatorSuccessPayload) {
        if (
            get(eventInfo, 'data.dashboardId') !== this.dashboardId ||
            get(eventInfo, 'data.sectionId') !== this.sectionId ||
            get(eventInfo, 'data.componentId') !== this.id
        )
            return;

        const response: Record<RuleResponseTypes, () => any> = {
            ACTION: () => {
                this._eventBroker.dispatch(eventInfo);
            },
        };

        if (!response[eventInfo.data?.type]) return;

        response[eventInfo.data.type]?.();
    }
}
